import type { AmbientKind, CelebrationKind, FestivalTheme } from '@mendikot/design-tokens';
import type confettiFn from 'canvas-confetti';
import { useEffect, useRef } from 'react';

/**
 * Celebrations are the delight budget (rare: a won hand, a Mendikot, a match) — never on routine
 * actions. Reduced motion skips them entirely; the result sheet still says what happened.
 */
export function celebrate(theme: FestivalTheme, size: 'small' | 'big', reduce: boolean): void {
  if (reduce) return;
  void import('canvas-confetti').then((mod) => burst(mod.default, theme, size));
}

function burst(confetti: typeof confettiFn, theme: FestivalTheme, size: 'small' | 'big'): void {
  const p = theme.palette;
  const colors = [p.accent, p.accentStrong, p.teamA, p.cardFace, p.teamB];
  const count = size === 'big' ? 160 : 60;
  const kind: CelebrationKind = theme.celebration;
  const base = { disableForReducedMotion: true, zIndex: 80, colors } as const;
  switch (kind) {
    case 'fireworks': {
      const bursts = size === 'big' ? 5 : 2;
      for (let i = 0; i < bursts; i++) {
        setTimeout(() => {
          void confetti({
            ...base,
            particleCount: 70,
            startVelocity: 32,
            spread: 360,
            ticks: 90,
            gravity: 0.7,
            scalar: 0.9,
            origin: { x: 0.2 + Math.random() * 0.6, y: 0.2 + Math.random() * 0.3 },
          });
        }, i * 260);
      }
      return;
    }
    case 'colors':
      void confetti({
        ...base,
        colors: ['#ff5fa2', '#ffd23f', '#3ec1d3', '#7bd389', '#a066ff'],
        particleCount: count,
        spread: 110,
        scalar: 1.8,
        shapes: ['circle'],
        origin: { y: 0.7 },
      });
      return;
    case 'tricolor':
      void confetti({
        ...base,
        colors: ['#ff9933', '#ffffff', '#138808'],
        particleCount: count,
        spread: 90,
        origin: { y: 0.7 },
      });
      return;
    case 'petals':
      void confetti({
        ...base,
        colors: ['#ffb000', '#ff7a00', '#ffd166', '#e4572e'],
        particleCount: count,
        spread: 120,
        gravity: 0.55,
        drift: 0.4,
        scalar: 1.2,
        shapes: ['circle'],
        origin: { y: 0.6 },
      });
      return;
    case 'snow':
      void confetti({
        ...base,
        colors: ['#ffffff', '#e6f0ff', p.accent],
        particleCount: count,
        spread: 140,
        gravity: 0.4,
        scalar: 0.9,
        shapes: ['circle'],
        origin: { y: 0.5 },
      });
      return;
    default:
      void confetti({ ...base, particleCount: count, spread: 80, origin: { y: 0.7 } });
  }
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  a: number;
  spin: number;
  color: string;
}

/**
 * Low-key festival ambience behind the UI: a handful of slow particles on a canvas, paused when the
 * tab is hidden and absent under reduced motion. Purely decorative.
 */
export function Ambient({ theme, reduce }: { theme: FestivalTheme; reduce: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const kind: AmbientKind = theme.ambient.kind;
  const intensity = theme.ambient.intensity;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || reduce || kind === 'none' || intensity <= 0) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    let width = 0;
    let height = 0;
    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const palette = theme.palette;
    const colorsFor: Record<AmbientKind, string[]> = {
      none: [],
      sparks: [palette.accent, palette.accentStrong],
      diyas: ['#ffb627', '#ff8c1a', '#ffd166'],
      lanterns: [palette.accent, '#ffe9a8'],
      gulal: ['#ff5fa2', '#ffd23f', '#3ec1d3', '#7bd389', '#a066ff'],
      petals: ['#ffb000', '#ff7a00', '#ffd166', '#f25c54'],
      kites: ['#e4572e', '#ffcc33', '#2a9d8f', '#ff5fa2'],
      snow: ['#ffffff', '#dfe9ff'],
      confetti: ['#ff9933', '#ffffff', '#138808'],
    };
    const colors = colorsFor[kind];
    const rising = kind === 'diyas' || kind === 'lanterns' || kind === 'sparks';
    const count = Math.round((kind === 'snow' ? 34 : kind === 'kites' ? 5 : 18) * intensity);
    const spawn = (initial: boolean): Particle => ({
      x: Math.random() * width,
      y: initial ? Math.random() * height : rising ? height + 20 : -20,
      vx: (Math.random() - 0.5) * (kind === 'kites' ? 0.5 : 0.25),
      vy: (rising ? -1 : 1) * (0.15 + Math.random() * (kind === 'snow' ? 0.5 : 0.3)),
      r:
        kind === 'gulal'
          ? 18 + Math.random() * 30
          : kind === 'kites'
            ? 10 + Math.random() * 6
            : 1.5 + Math.random() * 3,
      a: Math.random() * Math.PI * 2,
      spin: (Math.random() - 0.5) * 0.02,
      color: colors[Math.floor(Math.random() * colors.length)]!,
    });
    const particles = Array.from({ length: count }, () => spawn(true));
    let frame = 0;
    let last = performance.now();

    const draw = (now: number) => {
      const dt = Math.min(48, now - last) / 16.7;
      last = now;
      ctx.clearRect(0, 0, width, height);
      for (const p of particles) {
        p.x += p.vx * dt + Math.sin(p.a) * 0.15;
        p.y += p.vy * dt;
        p.a += p.spin * dt;
        if ((rising && p.y < -40) || (!rising && p.y > height + 40)) Object.assign(p, spawn(false));
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.a);
        ctx.fillStyle = p.color;
        if (kind === 'gulal') {
          ctx.globalAlpha = 0.08;
          ctx.beginPath();
          ctx.arc(0, 0, p.r, 0, Math.PI * 2);
          ctx.fill();
        } else if (kind === 'petals') {
          ctx.globalAlpha = 0.55;
          ctx.beginPath();
          ctx.ellipse(0, 0, p.r * 1.8, p.r, 0, 0, Math.PI * 2);
          ctx.fill();
        } else if (kind === 'kites') {
          ctx.globalAlpha = 0.35;
          ctx.beginPath();
          ctx.moveTo(0, -p.r);
          ctx.lineTo(p.r * 0.7, 0);
          ctx.lineTo(0, p.r * 1.3);
          ctx.lineTo(-p.r * 0.7, 0);
          ctx.closePath();
          ctx.fill();
        } else if (kind === 'confetti') {
          ctx.globalAlpha = 0.5;
          ctx.fillRect(-p.r, -p.r * 0.5, p.r * 2, p.r);
        } else {
          ctx.globalAlpha = kind === 'snow' ? 0.6 : 0.75;
          ctx.shadowColor = p.color;
          ctx.shadowBlur = rising ? 10 : 0;
          ctx.beginPath();
          ctx.arc(0, 0, p.r, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }
      frame = requestAnimationFrame(draw);
    };

    const onVisibility = () => {
      cancelAnimationFrame(frame);
      if (document.visibilityState === 'visible') {
        last = performance.now();
        frame = requestAnimationFrame(draw);
      }
    };
    frame = requestAnimationFrame(draw);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVisibility);
      ctx.clearRect(0, 0, width, height);
    };
  }, [kind, intensity, reduce, theme.palette]);

  if (reduce || kind === 'none') return null;
  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none fixed inset-0 z-0 h-full w-full"
      aria-hidden="true"
    />
  );
}
